import * as d3 from "d3"

export default class BarDrawer {
    static drawBars(args) {
        return new BarDrawer(args).draw()
    }

    static drawLegends({ bars, legendsContainer, options = {} }) {
        legendsContainer.replaceChildren(...bars.map((bar, index) => this.legendFor(bar, index, options)))
    }

    static legendFor(bar, index, options = {}) {
        const legend = document.createElement("div")
        legend.className = "line-chart-legend"
        legend.dataset.barKey = bar.key
        legend.innerHTML = `
            <div class="legend-details">
                <div class="legend-row">
                    <div class="legend-color" style="background-color:${bar.options.color};"></div>
                    <div class="legend-name"></div>
                </div>
                ${ this.hoverLegendValuesTemplate(index, options) }
            </div>
        `
        legend.querySelector(".legend-name").textContent = bar.options.label

        return legend
    }

    static hoverLegendValuesTemplate(index, options) {
        if (!options.show_hover_value && !options.show_hover_percentage) return ""

        return `
                <div class="legend-row">
                    <div class="legend-color legend-transparent"></div>
                    <div class="legend-values" data-index="${index}">
                        ${ options.show_hover_value ? '<div class="legend-data">--</div>' : '' }
                        ${ options.show_hover_percentage ? '<div class="legend-percentage">--%</div>' : '' }
                    </div>
                </div>
        `
    }

    constructor({ g, xAxis, yAxis, bars, options = {} }) {
        this.g = g
        this.svg = d3.select(g.node().ownerSVGElement)
        this.xScale = xAxis.scale
        this.yScale = yAxis.scale
        this.bars = bars
        this.options = options
    }

    draw() {
        if (this.options.stacked) {
            this.drawStackedBars()
        } else {
            this.drawGroupedBars()
        }
    }

    drawGroupedBars() {
        const barScale = d3.scaleBand()
            .domain(this.bars.map(bar => bar.key))
            .range([0, this.xScale.bandwidth()])
            .padding(0.12)

        this.bars.forEach(bar => {
            const seriesGroup = this.g
                .append("g")
                .attr("class", "bar-series")
                .attr("data-bar-key", bar.key)

            seriesGroup
                .selectAll(".bar")
                .data(bar.data)
                .join("rect")
                .attr("class", "bar")
                .attr("x", ([x]) => this.xScale(x) + barScale(bar.key))
                .attr("y", ([, y]) => this.yScale(Math.max(y, 0)))
                .attr("width", barScale.bandwidth())
                .attr("height", ([, y]) => Math.abs(this.yScale(y) - this.yScale(0)))
                .attr("fill", this.fillFor(bar))

            if (this.options.show_percentage) this.drawGroupedPercentages(seriesGroup, bar, barScale)
        })
    }

    drawGroupedPercentages(seriesGroup, bar, barScale) {
        seriesGroup
            .selectAll(".bar-percentage")
            .data(bar.data)
            .join("text")
            .attr("class", (_datum, index) => `legend-percentage bar-label ${this.percentageTrendClass(bar.data, index)}`)
            .attr("x", ([x]) => this.xScale(x) + barScale(bar.key) + (barScale.bandwidth() / 2))
            .attr("y", ([, y]) => Math.max(12, this.yScale(Math.max(y, 0)) - 6))
            .attr("text-anchor", "middle")
            .text((_datum, index) => this.percentageText(bar.data, index))
    }

    drawStackedBars() {
        const totalsByX = new Map()

        this.bars.forEach(bar => {
            this.g
                .append("g")
                .attr("class", "bar-series")
                .attr("data-bar-key", bar.key)
                .selectAll(".bar")
                .data(this.stackedDataFor(bar, totalsByX))
                .join("rect")
                .attr("class", "bar")
                .attr("x", datum => this.xScale(datum.x))
                .attr("y", datum => this.yScale(datum.y1))
                .attr("width", this.xScale.bandwidth())
                .attr("height", datum => Math.abs(this.yScale(datum.y0) - this.yScale(datum.y1)))
                .attr("fill", this.fillFor(bar))
        })
    }

    fillFor(bar) {
        if (!bar.options.gradient) return bar.options.color

        return `url(#${this.gradientIdFor(bar)})`
    }

    gradientIdFor(bar) {
        const gradientId = `bar-gradient-${this.safeIdFor(bar.key)}`
        let defs = this.svg.select("defs.chart-definitions")

        if (defs.empty()) {
            defs = this.svg
                .append("defs")
                .attr("class", "chart-definitions")
        }

        if (!defs.select(`#${gradientId}`).empty()) return gradientId

        const gradient = defs
            .append("linearGradient")
            .attr("id", gradientId)
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "0%")
            .attr("y2", "100%")

        gradient
            .append("stop")
            .attr("offset", "0%")
            .attr("stop-color", bar.options.color)
            .attr("stop-opacity", 1)

        gradient
            .append("stop")
            .attr("offset", "100%")
            .attr("stop-color", bar.options.color)
            .attr("stop-opacity", bar.options.gradient_opacity ?? 0.2)

        return gradientId
    }

    safeIdFor(value) {
        return String(value).replace(/[^a-zA-Z0-9_-]/g, "-")
    }

    stackedDataFor(bar, totalsByX) {
        return bar.data.map(([x, y]) => {
            const y0 = totalsByX.get(x) || 0
            const y1 = y0 + y
            totalsByX.set(x, y1)

            return { x, y0, y1 }
        })
    }

    percentageFor(data, index) {
        const oldValue = data[index - 1]?.[1]
        const newValue = data[index]?.[1]

        if (oldValue === undefined) return 0
        if (oldValue === 0) return newValue > 0 ? -1 : 0

        return (oldValue - newValue) / oldValue
    }

    percentageText(data, index) {
        const percentage = this.percentageFor(data, index)
        const factor = 10 ** this.options.percentage_precision
        const sign = percentage === 0 ? "" : (percentage < 0 ? "+" : "-")

        return `${sign}${Math.abs(Math.round(percentage * factor * 100) / factor)}%`
    }

    percentageTrendClass(data, index) {
        const percentage = this.percentageFor(data, index)

        if (percentage < 0) return "positive"
        if (percentage > 0) return "negative"

        return ""
    }
}
